import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ArrowDown, ArrowUp, Users } from "lucide-react";
import { formatDistanceKm, formatDuration, formatNumber } from "@/lib/format";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/cn";
import { RatingStars } from "@/features/reports/components/RatingStars";
import type { DriverReportRow } from "@/types/reports";

interface Props {
  rows: DriverReportRow[];
  isLoading: boolean;
  visibleKeys: string[];
}

const SKELETON_ROWS = 5;
const DASH = "—";

/** Sort keys map 1:1 to numeric/string row fields used for client-side sort. */
type SortKey =
  | "driver"
  | "routes"
  | "completedOrders"
  | "completedPct"
  | "onTime"
  | "avgLate"
  | "avgService"
  | "plannedKm"
  | "actualKm"
  | "sites"
  | "rating";

type SortDir = "asc" | "desc";

function formatDurationCell(mins: number | null, t: TFunction): string {
  if (mins === null) return DASH;
  return formatDuration(mins, t);
}

/** Local copy (do NOT refactor RoutesReportTable). On-time keeps the rose floor
 * because a low on-time rate is a genuine alert metric. */
function pctClass(v: number | null): string {
  if (v === null) return "text-gray-400";
  if (v >= 90) return "text-emerald-600 font-semibold";
  if (v >= 70) return "text-amber-600 font-semibold";
  return "text-rose-600 font-semibold";
}

/**
 * Colour ramp for the completion-% column (TASK-3840). Unlike on-time, a merely
 * low completion rate is not an error, so the rose floor is replaced by amber —
 * the ramp is amber (low) → amber (mid) → emerald (high), never rose.
 */
function completedPctClass(v: number | null): string {
  if (v === null) return "text-gray-400";
  if (v >= 90) return "text-emerald-600 font-semibold";
  return "text-amber-600 font-semibold";
}

function dash() {
  return <span className="text-gray-300">{DASH}</span>;
}

/** Extracts the sortable scalar for a row + key; null sorts last regardless of dir. */
function sortValue(row: DriverReportRow, key: SortKey): number | string | null {
  switch (key) {
    case "driver":
      return row.driver_name;
    case "routes":
      return row.routes_count;
    case "completedOrders":
      return row.completed_orders;
    case "completedPct":
      return row.completed_orders_percent;
    case "onTime":
      return row.on_time_percent;
    case "avgLate":
      return row.avg_late_min;
    case "avgService":
      return row.avg_service_time_min;
    case "plannedKm":
      return Number(row.planned_km);
    case "actualKm":
      return row.actual_km;
    case "sites":
      return row.sites_visited;
    case "rating":
      return row.avg_rating;
  }
}

function renderDriverCell(
  key: string,
  row: DriverReportRow,
  t: TFunction,
): React.ReactNode {
  switch (key) {
    case "driver":
      return (
        <span className="font-semibold text-gray-900">{row.driver_name}</span>
      );
    case "routes":
      return (
        <span className="text-gray-500">{formatNumber(row.routes_count)}</span>
      );
    case "completedOrders":
      return (
        <span className="text-gray-500">
          {formatNumber(row.completed_orders)}
        </span>
      );
    case "completedPct":
      return (
        <span className={completedPctClass(row.completed_orders_percent)}>
          {row.completed_orders_percent != null
            ? `${row.completed_orders_percent}%`
            : DASH}
        </span>
      );
    case "onTime":
      return (
        <span className={pctClass(row.on_time_percent)}>
          {row.on_time_percent != null ? `${row.on_time_percent}%` : DASH}
        </span>
      );
    case "avgLate":
      return row.avg_late_min != null ? (
        <span className="text-gray-500">
          {formatDurationCell(row.avg_late_min, t)}
        </span>
      ) : (
        dash()
      );
    case "avgService":
      return row.avg_service_time_min != null ? (
        <span className="text-gray-500">
          {formatDurationCell(row.avg_service_time_min, t)}
        </span>
      ) : (
        dash()
      );
    case "plannedKm": {
      const km = Number(row.planned_km);
      return Number.isFinite(km) ? (
        <span className="font-medium text-gray-700">
          {formatDistanceKm(km)}
        </span>
      ) : (
        dash()
      );
    }
    case "actualKm":
      return row.actual_km != null ? (
        <span className="font-medium text-gray-700">
          {formatDistanceKm(row.actual_km)}
        </span>
      ) : (
        dash()
      );
    case "sites":
      return (
        <span className="text-gray-500">{formatNumber(row.sites_visited)}</span>
      );
    case "rating":
      return row.avg_rating != null ? (
        <span className="inline-flex items-center gap-1.5">
          <RatingStars value={row.avg_rating} />
          <span className="text-xs font-medium text-gray-700">
            {row.avg_rating.toFixed(1)}
          </span>
          <span className="text-xs text-gray-400">({row.ratings_count})</span>
        </span>
      ) : (
        dash()
      );
    default:
      return null;
  }
}

/**
 * Driver scorecard table (TASK-3729 + TASK-3733). Client-side sortable, no
 * pagination (driver counts are small). Default sort: on-time % desc, nulls
 * always last. Columns are toggled via the shared column-visibility system.
 */
export function DriversReportTable({ rows, isLoading, visibleKeys }: Props) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>("onTime");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // `numeric` right-aligns the column header + cells and applies tabular-nums
  // (TASK-3840). The driver-name and rating (star widget) columns stay left.
  const COLUMNS: { key: SortKey; label: string; numeric?: boolean }[] = [
    { key: "driver", label: t("reports.driversTable.driver") },
    { key: "routes", label: t("reports.driversTable.routes"), numeric: true },
    {
      key: "completedOrders",
      label: t("reports.driversTable.completedOrders"),
      numeric: true,
    },
    {
      key: "completedPct",
      label: t("reports.driversTable.completedPct"),
      numeric: true,
    },
    { key: "onTime", label: t("reports.driversTable.onTime"), numeric: true },
    { key: "avgLate", label: t("reports.driversTable.avgLate"), numeric: true },
    {
      key: "avgService",
      label: t("reports.driversTable.avgService"),
      numeric: true,
    },
    {
      key: "plannedKm",
      label: t("reports.driversTable.plannedKm"),
      numeric: true,
    },
    {
      key: "actualKm",
      label: t("reports.driversTable.actualKm"),
      numeric: true,
    },
    { key: "sites", label: t("reports.driversTable.sites"), numeric: true },
    { key: "rating", label: t("reports.driversTable.rating") },
  ];

  // Hide the all-empty "Actual km" column when no route in range has actuals —
  // an all-em-dash column reads as a data bug. It reappears as soon as any row
  // reports an actual-km value (TASK-3848).
  const hasActualKm = useMemo(
    () => rows.some((row) => row.actual_km != null),
    [rows],
  );

  const visibleCols = COLUMNS.filter(
    (col) =>
      visibleKeys.includes(col.key) && (col.key !== "actualKm" || hasActualKm),
  );

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      // Nulls always sort last, regardless of direction.
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      let cmp: number;
      if (typeof av === "string" && typeof bv === "string") {
        cmp = av.localeCompare(bv);
      } else {
        cmp = (av as number) - (bv as number);
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/60">
            {visibleCols.map((col) => {
              const active = col.key === sortKey;
              return (
                <th
                  key={col.key}
                  className={cn(
                    "px-5 py-3.5 text-xs font-semibold uppercase tracking-widest text-gray-400",
                    col.numeric ? "text-right" : "text-left",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => handleSort(col.key)}
                    className={cn(
                      "inline-flex items-center gap-1 uppercase tracking-widest transition-colors hover:text-gray-600",
                      col.numeric && "flex-row-reverse",
                      active && "text-gray-700",
                    )}
                  >
                    {col.label}
                    {active &&
                      (sortDir === "asc" ? (
                        <ArrowUp size={12} />
                      ) : (
                        <ArrowDown size={12} />
                      ))}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <tr key={i} className="border-b border-gray-50 last:border-0">
                  {Array.from({ length: visibleCols.length }).map((_, j) => (
                    <td key={j} className="px-5 py-4">
                      <Skeleton className="h-4 w-20" />
                    </td>
                  ))}
                </tr>
              ))
            : sortedRows.map((row) => (
                <tr
                  key={row.driver_id}
                  className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50"
                >
                  {visibleCols.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        "px-5 py-4",
                        col.numeric && "text-right tabular-nums",
                      )}
                    >
                      {renderDriverCell(col.key, row, t)}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>

      {!isLoading && rows.length === 0 && (
        <EmptyState icon={Users} title={t("reports.driversTable.noData")} />
      )}
    </div>
  );
}
